import { supabase } from '../lib/supabase'
import { TransactionOverride, TransactionOverrideInput } from '../types/database'
import { OverrideHistoryService, OverrideSource } from './override-history.service'

export class TransactionOverridesNewService {
  private static readonly TABLE_NAME = 'transaction_overrides'

  /**
   * Get transaction override by transaction ID
   */
  static async getByTransactionId(transactionId: string): Promise<TransactionOverride | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('transaction_id', transactionId)
        .maybeSingle()

      if (error) {
        throw new Error(`Failed to fetch transaction override: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Create or update transaction override (upsert)
   */
  static async upsert(input: TransactionOverrideInput): Promise<TransactionOverride> {
    try {
      // Get current user for audit fields
      const user = await supabase.auth.getUser()
      const userId = user.data.user?.email || 'system'

      // Check if override already exists
      const existing = await this.getByTransactionId(input.transaction_id)

      let data: TransactionOverride

      if (existing) {
        // Update existing override
        const updateData = {
          ...input,
          updated_by: userId,
          updated_at: new Date().toISOString()
        }

        const { data: updatedData, error } = await supabase
          .from(this.TABLE_NAME)
          .update(updateData)
          .eq('transaction_id', input.transaction_id)
          .select()
          .single()

        if (error) {
          throw new Error(`Failed to update transaction override: ${error.message}`)
        }

        data = updatedData
      } else {
        // Create new override
        const insertData = {
          ...input,
          created_by: userId,
          updated_by: userId
        }

        const { data: insertedData, error } = await supabase
          .from(this.TABLE_NAME)
          .insert(insertData)
          .select()
          .single()

        if (error) {
          throw new Error(`Failed to create transaction override: ${error.message}`)
        }

        data = insertedData
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Update a single field on a transaction override with history logging.
   * This is the preferred method for inline editing as it logs changes.
   * @param transactionId - The transaction ID
   * @param fieldName - The field name to update (must be a valid TransactionOverride field)
   * @param newValue - The new value for the field
   * @param source - The source of the change (defaults to 'inline_edit')
   */
  static async updateField(
    transactionId: string,
    fieldName: keyof TransactionOverrideInput,
    newValue: unknown,
    source: OverrideSource = 'inline_edit'
  ): Promise<TransactionOverride> {
    try {
      // Get existing override to capture old value
      const existing = await this.getByTransactionId(transactionId)
      const oldValue = existing ? (existing as unknown as Record<string, unknown>)[fieldName] : null

      // Build the update data
      const updateData: TransactionOverrideInput = {
        transaction_id: transactionId,
        [fieldName]: newValue
      }

      // Perform the upsert
      const result = await this.upsert(updateData)

      // Log the change to history (don't await - fire and forget)
      OverrideHistoryService.logTransactionOverrideChange({
        transactionId,
        fieldName: fieldName as string,
        oldValue,
        newValue,
        source
      }).catch(() => { /* Failed to log history */ })

      return result
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete transaction override
   */
  static async delete(transactionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .delete()
        .eq('transaction_id', transactionId)

      if (error) {
        throw new Error(`Failed to delete transaction override: ${error.message}`)
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Update only sold prices (convenience method)
   */
  static async updateSoldPrice(
    transactionId: string,
    internalPrice?: number | null,
    externalPrice?: number | null
  ): Promise<TransactionOverride> {
    try {
      const input: TransactionOverrideInput = {
        transaction_id: transactionId
      }

      if (internalPrice !== undefined) {
        input.sale_price = internalPrice
      }

      if (externalPrice !== undefined) {
        input.external_sale_price = externalPrice
      }

      return await this.upsert(input)
    } catch (error) {
      throw error
    }
  }

  /**
   * Get effective data for a transaction (combines snapshot + overrides)
   * This merges the original transaction snapshot with any override data
   */
  static getEffectiveTransactionData(
    transaction: any,
    override: TransactionOverride | null
  ): any {
    if (!override) {
      return transaction
    }

    // Merge override data on top of transaction data
    // Override values take precedence when they are not null/undefined
    const effective = { ...transaction }

    Object.keys(override).forEach(key => {
      if (key === 'override_id' || key === 'transaction_id' || 
          key === 'created_at' || key === 'updated_at' || 
          key === 'created_by' || key === 'updated_by') {
        // Skip metadata fields
        return
      }

      const overrideValue = (override as any)[key]
      if (overrideValue !== null && overrideValue !== undefined) {
        effective[key] = overrideValue
      }
    })

    return effective
  }

  /**
   * Get all transaction overrides for a model (for bulk operations)
   */
  static async getByModelId(modelId: number): Promise<TransactionOverride[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select(`
          *,
          aircraft_transactions!inner(model_id)
        `)
        .eq('aircraft_transactions.model_id', modelId)

      if (error) {
        throw new Error(`Failed to fetch transaction overrides: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get transaction overrides for multiple transaction IDs
   * Returns a Map for easy lookup by transaction_id
   */
  static async getByTransactionIds(transactionIds: string[]): Promise<Map<string, TransactionOverride>> {
    try {
      if (!transactionIds || transactionIds.length === 0) {
        return new Map()
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .in('transaction_id', transactionIds)

      if (error) {
        throw new Error(`Failed to fetch transaction overrides: ${error.message}`)
      }

      const overrideMap = new Map<string, TransactionOverride>()
      if (data) {
        data.forEach((override: TransactionOverride) => {
          overrideMap.set(override.transaction_id, override)
        })
      }

      return overrideMap
    } catch (error) {
      throw error
    }
  }
}
